import { Editor } from "@shared/components/Editor";
import { MediaUploader } from "@shared/components/media/MediaUploader";
import { Button } from "@shared/components/ui/Button";
import { getProfileImage } from "@shared/hooks/useProfile";
import { useProfanityStore } from "@shared/services/profanity.service";
import { MediaFile, MediaType } from "@shared/types/media.types";
import { UserProfile } from "@shared/types/user";
import { containsProfanity } from "@shared/utils/profanityFilter";
import theme from "@shared/utils/themeColors";
import { ChartColumn, Lock, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { CreatePollData, normalizePoll, PollCreator, toCreatePollData } from "@features/polls";
import { fetchUserConnections } from "../../community/services/connections.service";
import { SpaceService } from "../services/space.service";
import { useSpacesStore } from "../store/useSpacesStore";
import {
	MediaItem,
	Space,
	SpaceVisibility,
} from "../types/space.types";

interface CreateSpaceModalProps {
	isOpen: boolean;
	onClose: () => void;
	space?: Space;
}

export function CreateSpaceModal({
	isOpen,
	onClose,
	space,
}: CreateSpaceModalProps) {
	const { createSpace, fetchSpace } = useSpacesStore();
	const { openProfanityModal } = useProfanityStore();
	const [title, setTitle] = useState("");
	const [content, setContent] = useState("");
	const [visibility, setVisibility] = useState<SpaceVisibility>(
		SpaceVisibility.PUBLIC,
	);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
	const [showMediaUploader, setShowMediaUploader] = useState(false);
	const [pollData, setPollData] = useState<CreatePollData | null>(null);
	// Editing a space whose poll was removed has to say so; leaving the poll out
	// means "do not touch it".
	const [pollRemoved, setPollRemoved] = useState(false);
	const [showPollCreator, setShowPollCreator] = useState(false);
	const [isPrivate, setIsPrivate] = useState(false);
	const [allowedUsers, setAllowedUsers] = useState<UserProfile[]>([]);
	const [userSearch, setUserSearch] = useState("");
	const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
	const [isSearching, setIsSearching] = useState(false);
	const [hasSearched, setHasSearched] = useState(false);
	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const searchMembers = useCallback(async (query: string) => {
		if (query.trim().length < 2) {
			setSearchResults([]);
			setHasSearched(false);
			return;
		}
		setIsSearching(true);
		try {
			const response = await fetchUserConnections({ page: 1, pageSize: 10, search: query });
			const filtered = response.connections.filter(
				(m) => !allowedUsers.some((u) => u.id === m.id)
			);
			setSearchResults(filtered);
		} catch {
			setSearchResults([]);
		} finally {
			setIsSearching(false);
			setHasSearched(true);
		}
	}, [allowedUsers]);

	useEffect(() => {
		if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
		searchTimeoutRef.current = setTimeout(() => searchMembers(userSearch), 300);
		return () => {
			if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
		};
	}, [userSearch, searchMembers]);

	const addAllowedUser = (user: UserProfile) => {
		setAllowedUsers((prev) => [...prev, user]);
		setUserSearch("");
		setSearchResults([]);
	};

	const removeAllowedUser = (userId: string) => {
		setAllowedUsers((prev) => prev.filter((u) => u.id !== userId));
	};

	const handlePollSave = (data: CreatePollData) => {
		setPollData(data);
		setPollRemoved(false);
		setShowPollCreator(false);
	};

	const handlePollRemove = () => {
		setPollData(null);
		setPollRemoved(true);
		setShowPollCreator(false);
	};

	const handlePollCancel = () => {
		setShowPollCreator(false);
	};

	// Initialize form with space data when editing
	useEffect(() => {
		if (space) {
			setTitle(space.title);
			setContent(space.content);
			// The poll the space already has, so editing shows it instead of losing it.
			setPollData(space.poll ? toCreatePollData(normalizePoll(space.poll)) : null);
			setPollRemoved(false);
			setShowPollCreator(false);
			setVisibility(SpaceVisibility.PUBLIC);
			setIsPrivate(space.isPrivate ?? false);
			setAllowedUsers(
				(space.allowedUsersProfiles ?? []).map((p) => ({
					id: p.id ?? '',
					name: p.name ?? '',
					imageUrl: p.imageUrl,
					thumbnailUrl: p.thumbnailUrl,
				} as UserProfile))
			);

			// Convert existing media to MediaFile format
			if (space.media && space.media.length > 0) {
				const existingMedia = space.media.map((item, index) => {
					const url = item.variants?.[0]?.url || "";
					const isImage = item.type === MediaType.IMAGE;

					return {
						id: `existing-${index}`,
						file: new File([], isImage ? "image.jpg" : "video.mp4", {
							type: isImage ? "image/jpeg" : "video/mp4",
						}),
						preview: url,
						uploadedUrl: url,
						type: item.type,
						progress: 100,
					} as MediaFile;
				});

				setMediaFiles(existingMedia);
			}
		} else {
			// Reset form for creation
			setTitle("");
			setContent("");
			setVisibility(SpaceVisibility.PUBLIC);
			setMediaFiles([]);
			setPollData(null);
			setPollRemoved(false);
			setShowPollCreator(false);
			setIsPrivate(false);
			setAllowedUsers([]);
		}
	}, [space]);

	if (!isOpen) return null;

	const handleMediaUpload = (newMediaFiles: MediaFile[]) => {
		setMediaFiles([...mediaFiles, ...newMediaFiles]);
		setShowMediaUploader(false);
	};

	// Convert MediaFile to MediaItem for API
	const convertMediaFilesToItems = (): MediaItem[] => {
		return mediaFiles.map((file) => ({
			type: file.type,
			variants: [
				{
					url: file.uploadedUrl || file.preview,
					format: file.file.type.split("/")[1] || "jpeg",
					type: "original",
				},
			],
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!title.trim() || !content.trim()) {
			setError("Please complete all required fields");
			return;
		}

		if (containsProfanity(title) || containsProfanity(content)) {
			openProfanityModal();
			return;
		}

		try {
			setIsSubmitting(true);
			setError(null);

			const mediaItems = convertMediaFilesToItems();
			const spaceData = {
				title,
				content,
				visibility,
				media: mediaItems.length > 0 ? mediaItems : undefined,
				poll: pollData ?? (pollRemoved ? null : undefined),
				isPrivate,
				allowedUsers: isPrivate ? allowedUsers.map((u) => u.id) : [],
			};

			if (space) {
				// Update existing space
				const updatedSpace = await SpaceService.updateSpace(
					space.id,
					spaceData,
				);
				if (updatedSpace) {
					// Refresh space details
					await fetchSpace(space.id);
					toast.success("Space updated successfully");
					onClose();
				} else {
					throw new Error("Failed to update space");
				}
			} else {
				// Create new space
				await createSpace(spaceData);

				// Clear the form and close the modal
				setTitle("");
				setContent("");
				setVisibility(SpaceVisibility.PUBLIC);
				setIsPrivate(false);
				setAllowedUsers([]);
				setMediaFiles([]);
				setShowMediaUploader(false);
				onClose();
			}
		} catch (err) {
			setError(
				space
					? "Error updating space. Please try again."
					: "Error creating space. Please try again.",
			);
			console.error(
				space ? "Error updating space:" : "Error creating space:",
				err,
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			<div className="flex items-center justify-center min-h-screen px-4">
				<div
					className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
					onClick={onClose}
				></div>

				<div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 mx-auto">
					<div className="flex justify-between items-center mb-4">
						<h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
							{space ? "Edit space" : "Create new space"}
						</h2>
						<button
							className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
							onClick={onClose}
						>
							<svg
								className="h-6 w-6"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</button>
					</div>

					{error && (
						<div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-md">
							{error}
						</div>
					)}

					<form onSubmit={handleSubmit}>
						<div className="mb-4">
							<label
								htmlFor="title"
								className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
							>
								Title *
							</label>
							<input
								type="text"
								id="title"
								className="form-input w-full"
								placeholder="Title of your space"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								disabled={isSubmitting}
								required
							/>
						</div>

						<div className="mb-4">
							<label
								htmlFor="content"
								className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
							>
								Content *
							</label>
							<Editor
								initialValue={content}
								onChange={setContent}
								placeholder="What do you want to share?"
								disabled={isSubmitting}
							/>
						</div>

						{/* Media upload controls */}
						<div className="mb-4">
							<div className="flex justify-between items-center mb-2">
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
									Media
								</label>
								<button
									type="button"
									className={`text-sm ${theme.text.brand} ${theme.text.brandHover}`}
									onClick={() => setShowMediaUploader(!showMediaUploader)}
									disabled={isSubmitting}
								>
									{showMediaUploader ? "Cancel" : "Add media"}
								</button>
							</div>

							{/* Media Uploader */}
							{showMediaUploader && (
								<div className="mb-3">
									<MediaUploader
										onUploadComplete={handleMediaUpload}
										onError={(error) => {
											toast.error(error);
											setShowMediaUploader(false);
										}}
										maxFiles={4}
									/>
								</div>
							)}

							{/* Media preview */}
							{mediaFiles.length > 0 && (
								<div className="grid grid-cols-2 gap-2 mb-2">
									{mediaFiles.map((file, index) => (
										<div
											key={index}
											className="relative rounded-md overflow-hidden bg-gray-100 dark:bg-gray-700"
										>
											{file.type === MediaType.IMAGE ? (
												<img
													src={file.uploadedUrl || file.preview}
													alt=""
													className="w-full h-auto object-cover"
												/>
											) : (
												<video
													src={file.uploadedUrl || file.preview}
													className="w-full h-auto"
													controls
												/>
											)}
											<button
												type="button"
												className="absolute top-1 right-1 bg-gray-800 bg-opacity-60 rounded-full p-1 text-white"
												onClick={() => {
													setMediaFiles(
														mediaFiles.filter((_, i) => i !== index),
													);
												}}
											>
												<svg
													className="h-4 w-4"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M6 18L18 6M6 6l12 12"
													/>
												</svg>
											</button>
											{file.progress < 100 && (
												<div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
													<div
														className={`h-full ${theme.bg.brand}`}
														style={{ width: `${file.progress}%` }}
													></div>
												</div>
											)}
										</div>
									))}
								</div>
							)}
						</div>

						{/* Poll Preview */}
						{pollData && (
							<div className="mb-4 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
								<div className="flex items-start justify-between gap-2 mb-2">
									<span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-300">
										Poll · {pollData.questions.length}{" "}
										{pollData.questions.length === 1 ? "question" : "questions"} ·{" "}
										{pollData.voterVisibility === "anonymous"
											? "anonymous"
											: "names visible"}
									</span>
									<div className="flex items-center gap-2">
										<button
											type="button"
											onClick={() => setShowPollCreator(true)}
											className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
										>
											Edit
										</button>
										<button
											type="button"
											onClick={handlePollRemove}
											className="text-gray-500 hover:text-gray-700 dark:text-gray-300"
											aria-label="Remove poll"
										>
											<X size={14} />
										</button>
									</div>
								</div>

								<div className="space-y-3">
									{pollData.questions.map((question, questionIndex) => (
										<div key={question.id ?? questionIndex}>
											<div className="font-medium mb-1 text-sm text-gray-800 dark:text-gray-100">
												{pollData.questions.length > 1 && `${questionIndex + 1}. `}
												{question.text}
											</div>
											<div className="space-y-1">
												{question.options.map((option, optionIndex) => (
													<div
														key={option.id ?? optionIndex}
														className="bg-white dark:bg-gray-600 p-2 rounded text-sm"
													>
														{option.text}
													</div>
												))}
											</div>
										</div>
									))}
								</div>

								<div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
									Ends: {pollData.endsAt.toLocaleDateString()}{" "}
									{pollData.endsAt.toLocaleTimeString()}
								</div>
							</div>
						)}

						{/* Poll Creator */}
						{showPollCreator && (
							<div className="mb-4">
								<PollCreator
									initialPoll={pollData}
									ownerLabel="Space"
									onCancel={handlePollCancel}
									onSave={handlePollSave}
								/>
							</div>
						)}

						{/* Privacy toggle */}
						<div className="mb-4">
							<label className="flex items-center cursor-pointer gap-2">
								<div className="relative">
									<input
										type="checkbox"
										className="sr-only"
										checked={isPrivate}
										onChange={(e) => setIsPrivate(e.target.checked)}
										disabled={isSubmitting}
									/>
									<div className={`w-10 h-5 rounded-full transition-colors ${isPrivate ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
										}`}>
										<div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isPrivate ? 'translate-x-5' : ''
											}`} />
									</div>
								</div>
								<Lock size={14} className="text-gray-500" />
								<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
									Private Space
								</span>
							</label>

							{isPrivate && (
								<div className="mt-3 ml-1">
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
										Invite Members
									</label>
									<div className="relative">
										<input
											type="text"
											className="form-input w-full text-sm"
											placeholder="Search members by name..."
											value={userSearch}
											onChange={(e) => setUserSearch(e.target.value)}
											disabled={isSubmitting}
										/>
										{isSearching && (
											<div className="absolute right-3 top-1/2 -translate-y-1/2">
												<div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
											</div>
										)}
										{isSearching && (
											<div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg px-3 py-2">
												<span className="text-sm text-gray-500 dark:text-gray-400">Loading...</span>
											</div>
										)}
										{!isSearching && hasSearched && userSearch.trim().length >= 2 && searchResults.length === 0 && (
											<div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg px-3 py-2">
												<span className="text-sm text-gray-500 dark:text-gray-400">No results found for your search</span>
											</div>
										)}
										{searchResults.length > 0 && (
											<div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-40 overflow-y-auto">
												{searchResults.map((member) => (
													<button
														key={member.id}
														type="button"
														className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-600"
														onClick={() => addAllowedUser(member)}
													>
														<img
															src={getProfileImage(member)}
															alt=""
															className="w-6 h-6 rounded-full object-cover"
														/>
														<span className="text-gray-800 dark:text-gray-200">{member.name}</span>
													</button>
												))}
											</div>
										)}
									</div>

									{allowedUsers.length > 0 && (
										<div className="flex flex-wrap gap-2 mt-2">
											{allowedUsers.map((user) => (
												<span
													key={user.id}
													className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs"
												>
													<img
														src={getProfileImage(user)}
														alt=""
														className="w-4 h-4 rounded-full object-cover"
													/>
													{user.name}
													<button
														type="button"
														onClick={() => removeAllowedUser(user.id)}
														className="ml-0.5 hover:text-blue-900 dark:hover:text-blue-100"
													>
														<X size={12} />
													</button>
												</span>
											))}
										</div>
									)}
								</div>
							)}
						</div>

						<div className="flex items-center justify-between">
							{!pollData && !showPollCreator && (
								<button
									type="button"
									className={`inline-flex items-center text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200`}
									onClick={() => setShowPollCreator(true)}
									disabled={isSubmitting}
								>
									<ChartColumn className="mr-1" size={16} />
									<span>Add Poll</span>
								</button>
							)}
							<div className="flex-1" />
						</div>

						<div className="flex justify-end space-x-3 mt-2">
							<Button
								variant="secondary"
								type="button"
								onClick={onClose}
								disabled={isSubmitting}
							>
								Cancel
							</Button>
							<Button
								variant="primary"
								type="submit"
								disabled={isSubmitting}
								isLoading={isSubmitting}
								loadingText={space ? "Updating..." : "Publishing..."}
							>
								{space ? "Update" : "Publish"}
							</Button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
}
